<template>
  <div class="min-h-screen bg-[#F8FAFC] p-4 md:p-6 lg:p-8 font-sans text-slate-900 space-y-6 selection:bg-blue-100">
    
    <!-- EN-TÊTE PRINCIPAL ET SÉLECTEUR D'AGENCE -->
<header class="flex  lg:flex-row justify-between items-start lg:items-center gap-4 bg-white p-6 rounded-2xl border border-slate-200 shadow-sm relative">
  <div class="flex items-center gap-4 w-full lg:w-auto">
    <div class="w-10 h-10 bg-blue-600 rounded-xl flex items-center justify-center text-white shadow-md shadow-blue-500/20 shrink-0">
      <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-7h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
      </svg>
    </div>
    
    <div class="flex-1 lg:flex-none">
      <div class="flex items-center gap-2 text-xs font-semibold text-slate-400">
        <span>Medialink Solutions</span>
        <span>/</span>
        <span class="text-blue-600">Configuration Officine</span>
      </div>

      <!-- SÉLECTEUR AVANCÉ RECHERCHABLE (+1000 AGENCES) -->
      <div class="relative mt-1 w-full lg:w-80">
        <div 
          @click="isAgencyDropdownOpen = !isAgencyDropdownOpen"
          class="bg-slate-50 border border-slate-200 hover:border-blue-300 text-slate-900 font-bold px-3.5 py-2 rounded-xl text-xs flex items-center justify-between cursor-pointer transition-all"
        >
          <span class="truncate">{{ selectedAgencyName || 'Sélectionner une pharmacie...' }}</span>
          <svg class="w-4 h-4 text-slate-400 shrink-0 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
          </svg>
        </div>

        <!-- Dropdown Panel -->
        <div v-if="isAgencyDropdownOpen" class="absolute left-0 right-0 mt-2 bg-white border border-slate-200 rounded-2xl shadow-xl z-50 p-2 space-y-2 animate-scale-in">
          <input 
            type="text" 
            v-model="agencySearchQuery" 
            placeholder="Filtrer parmi les agences..." 
            class="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-medium focus:outline-none focus:border-blue-500"
            autofocus
          >
          <div class="max-h-60 overflow-y-auto space-y-1 custom-scrollbar">
            <div 
              v-for="agency in filteredAgencies" 
              :key="agency.id"
              @click="selectAgency(agency.id)"
              class="px-3 py-2 rounded-xl text-xs font-semibold text-slate-700 hover:bg-blue-50 hover:text-blue-600 cursor-pointer transition-colors truncate"
              :class="{ 'bg-blue-50 text-blue-600': selectedAgencyId === agency.id }"
            >
              💊 {{ agency.nom }}
            </div>
            <div v-if="filteredAgencies.length === 0" class="p-4 text-center text-xs text-slate-400">
              Aucune pharmacie trouvée
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Boutons d'action -->
  <div class="flex items-center gap-2.5 w-full lg:w-auto justify-end">
    <button v-if="activeTab === 'queues'" @click="showAddQueueModal = true" class="px-4 py-2.5 bg-white border border-slate-200 rounded-xl font-bold text-xs text-slate-700 hover:bg-slate-50 transition-all flex items-center gap-2 shadow-sm">
      <UIIcon name="ticket" :size="16" />
      Créer une File
    </button>
    <button v-if="activeTab === 'users'" @click="showAddUserModalState = true" class="px-4 py-2.5 bg-white border border-slate-200 rounded-xl font-bold text-xs text-slate-700 hover:bg-slate-50 transition-all flex items-center gap-2 shadow-sm">
      <UIIcon name="user" :size="16" />
      Ajouter Personnel
    </button>
    <button v-if="activeTab === 'counters'" @click="showAddCounterModal = true" class="px-4 py-2.5 bg-blue-600 text-white rounded-xl font-bold text-xs shadow-sm hover:bg-blue-700 transition-all">
      + Nouveau Guichet
    </button>
  </div>
</header>

    <!-- KPI GRID -->
    <div class="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
      <div v-for="(stat, i) in [
        { label: 'Guichets Actifs', val: counters.length, color: 'text-slate-900' },
        { label: 'Files / Services', val: queues.length, color: 'text-blue-600' },
        { label: 'Personnel Total', val: users.length, color: 'text-slate-900' },
        { label: 'Agents Libres', val: users.filter(u => !u.assignedCounterId).length, color: 'text-emerald-600' }
      ]" :key="i" class="p-5 bg-white rounded-2xl border border-slate-200 shadow-sm">
        <p class="text-[10px] font-bold uppercase text-slate-400 tracking-wider">{{ stat.label }}</p>
        <p class="text-2xl font-black mt-1 tabular-nums" :class="stat.color">{{ stat.val }}</p>
      </div>
    </div>

    <!-- ONGLETS DE NAVIGATION -->
    <div class="flex gap-8 border-b border-slate-200 px-2">
      <button @click="activeTab = 'counters'" :class="activeTab === 'counters' ? 'text-blue-600 border-b-2 border-blue-600 pb-3 font-bold' : 'text-slate-400 pb-3 font-semibold hover:text-slate-600'" class="text-xs uppercase tracking-wider transition-all flex items-center gap-2 outline-none">
        <UIIcon name="archive" :size="16" />
        Guichets & Caisses
      </button>
      <button @click="activeTab = 'queues'" :class="activeTab === 'queues' ? 'text-blue-600 border-b-2 border-blue-600 pb-3 font-bold' : 'text-slate-400 pb-3 font-semibold hover:text-slate-600'" class="text-xs uppercase tracking-wider transition-all flex items-center gap-2 outline-none">
        <UIIcon name="ticket" :size="16" />
        Files d'attente
      </button>
      <button @click="activeTab = 'users'" :class="activeTab === 'users' ? 'text-blue-600 border-b-2 border-blue-600 pb-3 font-bold' : 'text-slate-400 pb-3 font-semibold hover:text-slate-600'" class="text-xs uppercase tracking-wider transition-all flex items-center gap-2 outline-none">
        <UIIcon name="users" :size="16" />
        Personnel
      </button>
    </div>

    <main>
      
      <!-- ONGLET GUICHETS -->
      <div v-if="activeTab === 'counters'" class="space-y-3">
        <div v-for="counter in counters" :key="counter._id" class="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm flex  md:flex-row md:items-center justify-between gap-4 group hover:border-blue-200 transition-all">
          <div class="flex items-center gap-4">
            <div class="w-10 h-10 bg-slate-50 border border-slate-200 rounded-xl flex items-center justify-center font-bold text-xs text-slate-500 group-hover:bg-blue-50 group-hover:text-blue-600 group-hover:border-blue-200 transition-colors shrink-0">
              {{ counter.priorityOrder || 0 }}
            </div>
            <div>
              <h3 class="text-sm font-bold text-slate-900">{{ counter.name }}</h3>
              <div class="flex flex-wrap gap-1.5 mt-1.5">
                <span v-if="counter.defaultQueueId" class="text-[10px] bg-blue-50 text-blue-700 px-2 py-0.5 rounded-lg font-bold border border-blue-200 flex items-center gap-1">
                  <UIIcon name="star" :size="10" />
                  {{ getQueueName(counter.defaultQueueId) }} (Défaut)
                </span>
                <span v-for="sId in counter.serviceIds" :key="sId" v-show="sId !== counter.defaultQueueId" class="text-[10px] bg-slate-50 text-slate-600 px-2 py-0.5 rounded-lg font-bold border border-slate-200">
                  {{ getQueueName(sId) }}
                </span>
              </div>
            </div>
          </div>
          <div class="flex items-center justify-between md:justify-end gap-6 border-t md:border-t-0 pt-3 md:pt-0 border-slate-100">
            <div class="text-left md:text-right">
              <p class="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Agents</p>
              <p class="text-xs font-semibold text-slate-700">{{ getCounterUsers(counter._id).length }} rattaché(s)</p>
            </div>
            <button @click="openEditDrawer(counter)" class="px-4 py-2 bg-white border border-slate-200 text-slate-700 rounded-xl font-bold text-xs hover:text-blue-600 hover:border-blue-200 hover:bg-blue-50 transition-all shadow-sm">
              Configurer
            </button>
          </div>
        </div>
      </div>

      <!-- ONGLET FILES -->
      <div v-if="activeTab === 'queues'" class="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        <div v-for="queue in queues" :key="queue._id" class="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm relative overflow-hidden group hover:border-blue-200 transition-all flex flex-col justify-between">
          <div>
            <div v-if="queue.icon" class="absolute top-0 right-0 w-14 h-14 bg-blue-50 border-l border-b border-blue-100 rounded-bl-2xl flex items-center justify-center">
              <PharmacyIcon :name="queue.icon" :size="24" color="#2563eb" />
            </div>
            <h3 class="text-base font-bold text-slate-900 mb-1 capitalize">{{ queue.name }}</h3>
            <p class="text-[10px] text-slate-400 font-bold uppercase tracking-wider">Préfixe : {{ queue.prefix }}-XXX</p>
            <p class="text-[10px] text-slate-500 font-medium mt-1">
              Plage tickets :
              <span class="font-mono font-bold text-slate-700">
                {{ queue.rangeMin ?? 0 }} → {{ queue.rangeMax ?? 999 }}
              </span>
            </p>

            <!-- Badge de visibilité -->
            <div v-if="queue.visibilitySchedule?.enabled" class="mt-2">
              <span
                :class="[
                  'text-[10px] px-2 py-1 rounded-lg font-bold inline-flex items-center gap-1',
                  queue.isVisibleToday
                    ? 'bg-green-50 text-green-700 border border-green-200'
                    : 'bg-gray-100 text-gray-500 border border-gray-200'
                ]"
              >
                <span>{{ queue.isVisibleToday ? '✅' : '❌' }}</span>
                <span>{{ queue.isVisibleToday ? 'Visible aujourd\'hui' : 'Non visible aujourd\'hui' }}</span>
              </span>
            </div>
          </div>

          <div class="mt-6 flex justify-between items-center border-t border-slate-100 pt-3">
            <button @click="deleteQueue(queue._id)" class="text-xs font-bold text-slate-400 hover:text-rose-600 transition-colors">Supprimer</button>
            <button @click="openEditQueueModal(queue)" class="px-3 py-1.5 bg-slate-50 hover:bg-blue-50 hover:text-blue-600 rounded-xl font-bold text-xs transition-all border border-slate-200">
              Modifier
            </button>
          </div>
        </div>
      </div>

      <!-- ONGLET PERSONNEL -->
      <div v-if="activeTab === 'users'" class="space-y-4">
        <div class="bg-white p-4 rounded-2xl shadow-sm border border-slate-200 flex gap-3 items-center">
          <div class="flex-1 relative">
            <span class="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
            </span>
            <input type="text" v-model="userSearchQuery" placeholder="Rechercher un membre du personnel..." class="w-full pl-10 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:border-blue-500 outline-none text-xs font-medium transition-all">
          </div>
          <button v-if="userSearchQuery" @click="userSearchQuery = ''" class="px-3 py-2 text-slate-400 hover:text-slate-600 font-bold text-xs">
            Effacer
          </button>
        </div>

        <div class="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
          <table class="w-full text-left text-xs">
            <thead class="bg-slate-50/75 text-[10px] font-bold uppercase text-slate-400 border-b border-slate-100">
              <tr>
                <th class="px-6 py-4">Collaborateur</th>
                <th class="px-6 py-4">Rôle Système</th>
                <th class="px-6 py-4">Poste Assigné</th>
                <th class="px-6 py-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-slate-100">
              <tr v-for="user in paginatedUsers" :key="user._id" class="hover:bg-slate-50/60 transition-colors">
                <td class="px-6 py-4">
                  <div class="font-bold text-slate-900">{{ user.firstName }} {{ user.lastName }}</div>
                  <div class="text-[10px] text-slate-400 font-medium">{{ user.email }}</div>
                </td>
                <td class="px-6 py-4">
                  <select v-if="user.role !== 'AGENCY_ADMIN'" @change="updateUserRole(user, $event)"
                          :class="user.role === 'BACK_OFFICE' ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-blue-50 text-blue-700 border-blue-200'"
                          class="px-2.5 py-1 rounded-lg text-[10px] font-bold uppercase border outline-none cursor-pointer">
                    <option value="EMPLOYEE" :selected="user.role === 'EMPLOYEE'">EMPLOYEE</option>
                    <option value="BACK_OFFICE" :selected="user.role === 'BACK_OFFICE'">BACK OFFICE</option>
                  </select>
                  <span v-else class="bg-purple-50 text-purple-700 border-purple-200 px-2.5 py-1 rounded-lg text-[10px] font-bold uppercase border">
                    ADMIN
                  </span>
                </td>
                <td class="px-6 py-4">
                  <select @change="updateUserCounter(user, $event)" class="bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold py-1.5 px-3 focus:border-blue-500 outline-none text-slate-700">
                    <option :value="null">Non assigné</option>
                    <option v-for="c in counters" :key="c._id" :value="c._id" :selected="user.assignedCounterId === c._id">{{ c.name }}</option>
                  </select>
                </td>
                <td class="px-6 py-4 text-right">
                  <div class="flex justify-end gap-2">
                    <button @click="resetUserPassword(user)" class="p-2 bg-white border border-slate-200 text-slate-400 hover:text-amber-600 hover:border-amber-200 hover:bg-amber-50 rounded-xl transition-all shadow-sm" title="Réinitialiser mot de passe">
                      <UIIcon name="key" :size="14"/>
                    </button>
                    <button v-if="user.role !== 'AGENCY_ADMIN'" @click="deleteUser(user._id)" class="p-2 bg-white border border-slate-200 text-slate-400 hover:text-rose-600 hover:border-rose-200 hover:bg-rose-50 rounded-xl transition-all shadow-sm" title="Supprimer">
                      <UIIcon name="trash" :size="14" />
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
          <TablePagination v-if="filteredUsers.length > 0" :current-page="userCurrentPage" @update:current-page="userCurrentPage = $event" :total-items="filteredUsers.length" :items-per-page="userItemsPerPage" class="border-t border-slate-100 px-6 py-4" />
        </div>
      </div>
    </main>

    <!-- TIRETTE DE CONFIGURATION DU GUICHET -->
    <div v-if="selectedCounter" class="fixed inset-0 z-50 overflow-hidden">
      <div class="absolute inset-0 bg-slate-900/40 backdrop-blur-[2px]" @click="selectedCounter = null"></div>
      <div class="absolute right-0 top-0 bottom-0 w-full max-w-xl bg-white shadow-xl p-6 lg:p-8 overflow-y-auto border-l border-slate-200">
        <header class="flex justify-between items-center mb-6 pb-4 border-b border-slate-100">
          <div>
            <span class="text-[10px] font-bold uppercase text-blue-600 bg-blue-50 border border-blue-200 px-2.5 py-1 rounded-full">Guichet & Priorités</span>
            <h2 class="text-xl font-bold text-slate-900 mt-1.5">{{ selectedCounter.name }}</h2>
          </div>
          <button @click="selectedCounter = null" class="p-2 bg-slate-50 rounded-xl hover:bg-slate-100 text-slate-400">✕</button>
        </header>

        <div class="space-y-6 text-xs">
          <div>
            <label class="block font-bold text-slate-500 mb-1">Nom du guichet</label>
            <input v-model="selectedCounter.name" type="text" class="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl font-medium text-slate-800 focus:outline-none focus:border-blue-500" />
          </div>

          <div>
            <label class="block font-bold text-slate-500 mb-2">Files gérées (Priorité par ordre)</label>
            <div class="space-y-2">
              <div v-for="q in queues" :key="q._id"
                @click="toggleQueueToCounter(q._id)"
                :class="selectedCounter.serviceIds?.includes(q._id) ? 'border-blue-500 bg-blue-50/50 text-blue-700' : 'border-slate-200 bg-slate-50 text-slate-600'"
                class="p-3.5 rounded-xl border cursor-pointer flex justify-between items-center transition-all hover:border-blue-300"
              >
                <div class="flex items-center gap-3">
                  <div class="w-2 h-2 rounded-full" :class="selectedCounter.serviceIds?.includes(q._id) ? 'bg-blue-600' : 'bg-slate-300'"></div>
                  <span class="font-bold">{{ q.name }}</span>
                </div>
                <span v-if="selectedCounter.serviceIds?.includes(q._id)" class="text-[10px] bg-blue-600 text-white px-2 py-0.5 rounded font-bold">
                  PRIO {{ selectedCounter.serviceIds.indexOf(q._id) + 1 }}
                </span>
              </div>
            </div>
          </div>

          <div class="pt-4 border-t border-slate-100">
            <label class="block font-bold text-slate-500 mb-1">File d'Appel par défaut</label>
            <select v-model="selectedCounter.defaultQueueId" @change="updateDefaultQueue" class="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl font-medium text-slate-800 outline-none focus:border-blue-500">
              <option :value="null">Rotation intelligente (Priorités)</option>
              <option v-for="q in queues" :key="q._id" :value="q._id">{{ q.name }} ({{ q.prefix }})</option>
            </select>
          </div>

          <div class="pt-6 border-t border-slate-100 flex justify-between items-center">
             <button @click="deleteCounter(selectedCounter._id)" class="px-4 py-2.5 text-rose-600 font-bold hover:bg-rose-50 rounded-xl transition-colors border border-rose-200">Supprimer</button>
             <button @click="updateCounter" class="px-5 py-2.5 bg-blue-600 text-white rounded-xl font-bold shadow-sm hover:bg-blue-700 transition-all">Enregistrer</button>
          </div>
        </div>
      </div>
    </div>

    <!-- MODALE CRÉATION / MODIFICATION FILE -->
    <div v-if="showAddQueueModal || showEditQueueModal" class="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-y-auto">
      <div class="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" @click="showAddQueueModal = false; showEditQueueModal = false"></div>

      <div class="relative bg-white w-full max-w-5xl rounded-2xl shadow-xl border border-slate-200 my-8 flex flex-col max-h-[90vh]">

        <div class="px-6 py-4 border-b border-slate-200 flex justify-between items-center bg-slate-50 shrink-0">
          <div class="flex items-center gap-3">
            <div class="w-8 h-8 bg-blue-600 text-white rounded-xl flex items-center justify-center shadow-sm">
              <UIIcon name="ticket" :size="16"/>
            </div>
            <div>
              <h3 class="text-base font-bold text-slate-900">{{ showEditQueueModal ? 'Modifier' : 'Créer' }} une File d'Attente</h3>
              <p class="text-xs text-slate-400">Paramètres du service officine</p>
            </div>
          </div>
          <button @click="showAddQueueModal = false; showEditQueueModal = false" class="text-slate-400 hover:text-slate-600">✕</button>
        </div>

        <div class="flex-1 overflow-y-auto p-6">
          <div class="grid lg:grid-cols-2 gap-6">

            <div class="space-y-5 text-xs">
              <div class="bg-white border border-slate-200 rounded-2xl p-5 space-y-4 shadow-sm">
                <h4 class="font-bold text-slate-900 text-sm">Informations Générales</h4>
                <div>
                  <label class="block font-bold text-slate-500 mb-1">Nom du Service</label>
                  <input v-model="(showEditQueueModal ? editingQueue : newQueue).name" placeholder="ex: Ordonnances, Conseil..." class="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl font-medium text-slate-800 focus:outline-none focus:border-blue-500" />
                </div>
                <div>
                  <label class="block font-bold text-slate-500 mb-1">Code (Préfixe)</label>
                  <input v-model="(showEditQueueModal ? editingQueue : newQueue).prefix" maxlength="3" :disabled="(showEditQueueModal ? editingQueue : newQueue).iconDisplayMode === 'number_only'" class="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl font-bold uppercase focus:outline-none focus:border-blue-500" placeholder="A" />
                </div>
              </div>

              <div class="bg-white border border-slate-200 rounded-2xl p-5 space-y-4 shadow-sm">
                <div class="flex items-start justify-between gap-3">
                  <div>
                    <h4 class="font-bold text-slate-900 text-sm">Plage de tickets</h4>
                    <p class="text-[10px] text-slate-400 mt-1">Définissez le premier et le dernier numéro attribués à cette file.</p>
                  </div>
                  <span class="shrink-0 rounded-lg border border-blue-200 bg-blue-50 px-2.5 py-1 text-[10px] font-black text-blue-700">
                    {{ queueForm.rangeMin }} → {{ queueForm.rangeMax }}
                  </span>
                </div>

                <div class="grid grid-cols-2 gap-3">
                  <div>
                    <label class="block font-bold text-slate-500 mb-1">Premier numéro</label>
                    <input v-model.number="queueForm.rangeMin" type="number" min="0" max="9999" step="1"
                           class="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl font-mono font-bold text-slate-800 focus:outline-none focus:border-blue-500" />
                  </div>
                  <div>
                    <label class="block font-bold text-slate-500 mb-1">Dernier numéro</label>
                    <input v-model.number="queueForm.rangeMax" type="number" min="0" max="9999" step="1"
                           class="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl font-mono font-bold text-slate-800 focus:outline-none focus:border-blue-500" />
                  </div>
                </div>

                <p v-if="queueRangeInvalid" class="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-[10px] font-bold text-rose-600">
                  Le dernier numéro doit être strictement supérieur au premier numéro.
                </p>
                <p v-else class="text-[10px] text-slate-400">
                  Exemple : {{ queueForm.rangeMin }} → {{ queueForm.rangeMax }} génère les numéros
                  {{ queueRangePrefix }}{{ String(queueForm.rangeMin).padStart(3, '0') }} à
                  {{ queueRangePrefix }}{{ String(queueForm.rangeMax).padStart(3, '0') }}.
                </p>
              </div>

              <div class="bg-white border border-slate-200 rounded-2xl p-5 space-y-3 shadow-sm">
                <h4 class="font-bold text-slate-900 text-sm">Icône de la File</h4>
                <IconSelector v-model="(showEditQueueModal ? editingQueue : newQueue).icon" />
              </div>

              <div class="bg-white border border-slate-200 rounded-2xl p-5 space-y-3 shadow-sm">
                <h4 class="font-bold text-slate-900 text-sm">Format d'Affichage</h4>
                <div class="space-y-2">
                  <label class="flex items-center gap-2 p-2.5 bg-slate-50 rounded-xl border border-slate-200 cursor-pointer">
                    <input type="radio" v-model="(showEditQueueModal ? editingQueue : newQueue).iconDisplayMode" value="icon_prefix_number" class="text-blue-600" />
                    <span class="font-bold text-slate-800">Icône + Préfixe + Numéro</span>
                  </label>
                  <label class="flex items-center gap-2 p-2.5 bg-slate-50 rounded-xl border border-slate-200 cursor-pointer">
                    <input type="radio" v-model="(showEditQueueModal ? editingQueue : newQueue).iconDisplayMode" value="icon_number" class="text-blue-600" />
                    <span class="font-bold text-slate-800">Icône + Numéro uniquement</span>
                  </label>
                  <label class="flex items-center gap-2 p-2.5 bg-slate-50 rounded-xl border border-slate-200 cursor-pointer">
                    <input type="radio" v-model="(showEditQueueModal ? editingQueue : newQueue).iconDisplayMode" value="prefix_number" class="text-blue-600" />
                    <span class="font-bold text-slate-800">Préfixe + Numéro</span>
                  </label>
                  <label class="flex items-center gap-2 p-2.5 bg-slate-50 rounded-xl border border-slate-200 cursor-pointer">
                    <input type="radio" v-model="(showEditQueueModal ? editingQueue : newQueue).iconDisplayMode" value="number_only" class="text-blue-600" />
                    <span class="font-bold text-slate-800">Numéro uniquement</span>
                  </label>
                </div>
              </div>

              <!-- Planification de visibilité -->
              <QueueVisibilityScheduler
                v-model="(showEditQueueModal ? editingQueue : newQueue).visibilitySchedule"
              />
            </div>

            <div>
              <div class="sticky top-0">
                <TicketIconPreview
                  :iconName="(showEditQueueModal ? editingQueue : newQueue).icon"
                  :displayMode="(showEditQueueModal ? editingQueue : newQueue).iconDisplayMode"
                  :prefix="(showEditQueueModal ? editingQueue : newQueue).prefix || 'BR'"
                  :queueName="(showEditQueueModal ? editingQueue : newQueue).name || 'Nom du service'"
                />
              </div>
            </div>

          </div>
        </div>

        <div class="px-6 py-4 border-t border-slate-200 bg-slate-50 flex justify-end gap-3 shrink-0 text-xs">
          <button @click="showAddQueueModal = false; showEditQueueModal = false" class="px-4 py-2.5 border border-slate-200 bg-white rounded-xl font-bold hover:bg-slate-50">Annuler</button>
          <button @click="showEditQueueModal ? updateQueue() : createQueue()"
                  :disabled="queueRangeInvalid"
                  class="px-5 py-2.5 bg-blue-600 text-white rounded-xl font-bold shadow-sm hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-blue-600">
            {{ showEditQueueModal ? 'Mettre à jour' : 'Créer la file' }}
          </button>
        </div>

      </div>
    </div>

    <!-- MODALE NOUVEAU GUICHET -->
    <div v-if="showAddCounterModal" class="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div class="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" @click="showAddCounterModal = false"></div>
      <div class="relative bg-white w-full max-w-sm rounded-2xl p-6 shadow-xl border border-slate-200">
        <div class="w-12 h-12 bg-blue-50 text-blue-600 rounded-xl flex items-center justify-center mb-4">
          <UIIcon name="archive" :size="24" />
        </div>
        <h3 class="text-base font-bold text-slate-900 mb-1">Nouveau Guichet</h3>
        <p class="text-slate-400 text-xs mb-4">Identifiant de poste pour l'officine.</p>
        
        <div class="space-y-4 text-xs">
          <div>
            <label class="block font-bold text-slate-500 mb-1">Nom du guichet</label>
            <input v-model="newCounter.name" placeholder="ex: Guichet 1, Caisse A..." class="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl font-medium text-slate-800 focus:outline-none focus:border-blue-500" />
          </div>
          <div class="flex justify-end gap-2 pt-2">
            <button @click="showAddCounterModal = false" class="px-4 py-2 border border-slate-200 rounded-xl font-bold hover:bg-slate-50">Annuler</button>
            <button @click="createCounter" class="px-4 py-2 bg-blue-600 text-white rounded-xl font-bold shadow-sm hover:bg-blue-700">Créer</button>
          </div>
        </div>
      </div>
    </div>

    <!-- MODALE AJOUT PERSONNEL -->
    <div v-if="showAddUserModalState" class="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div class="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" @click="showAddUserModalState = false"></div>
      <div class="relative bg-white w-full max-w-md rounded-2xl p-6 shadow-xl border border-slate-200">
        <div class="flex justify-between items-center mb-4 pb-3 border-b border-slate-100">
          <h3 class="text-base font-bold text-slate-900">Ajouter un collaborateur</h3>
          <button @click="showAddUserModalState = false" class="text-slate-400 hover:text-slate-600">✕</button>
        </div>
        
        <div class="space-y-3 text-xs">
          <div class="grid grid-cols-2 gap-3">
            <div>
              <label class="block font-bold text-slate-500 mb-1">Prénom</label>
              <input v-model="newUser.firstName" class="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl font-medium text-slate-800 focus:outline-none focus:border-blue-500" />
            </div>
            <div>
              <label class="block font-bold text-slate-500 mb-1">Nom</label>
              <input v-model="newUser.lastName" class="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl font-medium text-slate-800 focus:outline-none focus:border-blue-500" />
            </div>
          </div>
          <div>
            <label class="block font-bold text-slate-500 mb-1">Email pro</label>
            <input v-model="newUser.email" type="email" class="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl font-medium text-slate-800 focus:outline-none focus:border-blue-500" />
          </div>
          <div>
            <label class="block font-bold text-slate-500 mb-1">Rôle</label>
            <select v-model="newUser.role" class="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl font-medium text-slate-800 focus:outline-none focus:border-blue-500">
              <option value="EMPLOYEE">Agent</option>
              <option value="BACK_OFFICE">Back Office</option>
              <option value="AGENCY_ADMIN">Administrateur</option>
            </select>
          </div>
          <div>
            <label class="block font-bold text-slate-500 mb-1">Mot de passe temporaire</label>
            <input v-model="newUser.password" type="password" class="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl font-medium text-slate-800 focus:outline-none focus:border-blue-500" />
          </div>
          
          <div class="flex justify-end gap-2 pt-3">
            <button @click="showAddUserModalState = false" class="px-4 py-2 border border-slate-200 rounded-xl font-bold hover:bg-slate-50">Annuler</button>
            <button @click="createUser" class="px-4 py-2 bg-blue-600 text-white rounded-xl font-bold shadow-sm hover:bg-blue-700">Créer</button>
          </div>
        </div>
      </div>
    </div>

    <!-- MODALE CLÉ DE RÉINITIALISATION -->
    <div v-if="resetData.show" class="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div class="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" @click="resetData.show = false"></div>
      <div class="relative bg-white w-full max-w-sm rounded-2xl p-6 shadow-xl border border-slate-200 text-xs">
        <h3 class="text-base font-bold text-slate-900 mb-1">Clé de réinitialisation</h3>
        <p class="text-slate-400 mb-4 truncate">Agent : {{ resetData.email }}</p>
        
        <div @click="copyToClipboard(resetData.key)" class="bg-slate-50 p-4 rounded-xl border border-slate-200 text-center cursor-pointer mb-4">
          <span class="text-lg font-mono font-bold text-blue-600 select-all">{{ resetData.key }}</span>
          <p class="text-[10px] text-slate-400 mt-1 uppercase font-bold">{{ copied ? 'Copié !' : 'Cliquer pour copier' }}</p>
        </div>

        <button @click="resetData.show = false" class="w-full py-2.5 bg-slate-900 text-white rounded-xl font-bold">Fermer</button>
      </div>
    </div>

  </div>
</template>

<script>
import { toast } from 'vue3-toastify';
import TablePagination from '@/components/TablePagination.vue';
import IconSelector from '@/components/IconSelector.vue';
import PharmacyIcon from '@/components/PharmacyIcons.vue';
import UIIcon from '@/components/UIIcons.vue';
import TicketIconPreview from '@/components/TicketIconPreview.vue';
import QueueVisibilityScheduler from '@/components/QueueVisibilityScheduler.vue';

export default {
  components: {
    TablePagination,
    IconSelector,
    PharmacyIcon,
    UIIcon,
    TicketIconPreview,
    QueueVisibilityScheduler
  },
  data() {
    return {
      activeTab: 'counters',
      isAgencyDropdownOpen: false,
agencySearchQuery: '',
      selectedAgencyId: null,
      agencies: [],
      counters: [],
      users: [],
      queues: [],
      showEditQueueModal: false,
      editingQueue: {
        _id: null,
        name: "",
        prefix: "",
        icon: "pill",
        rangeMin: 0,
        rangeMax: 999,
        visibilitySchedule: {
          enabled: false,
          days: {
            monday: true,
            tuesday: true,
            wednesday: true,
            thursday: true,
            friday: true,
            saturday: true,
            sunday: true
          }
        }
      },
      copied: false,

      userSearchQuery: '',
      userCurrentPage: 1,
      userItemsPerPage: 25,

      resetData: {
        show: false,
        key: '',
        email: '',
        expiresAt: null
      },

      selectedCounter: null,
      showAddCounterModal: false,
      showAddUserModalState: false,
      showAddQueueModal: false,

      newCounter: { name: "", serviceIds: [], active: true },
      newQueue: {
        name: "",
        prefix: "",
        icon: "pill",
        iconDisplayMode: "icon_prefix_number",
        rangeMin: 0,
        rangeMax: 999,
        visibilitySchedule: {
          enabled: false,
          days: {
            monday: true,
            tuesday: true,
            wednesday: true,
            thursday: true,
            friday: true,
            saturday: true,
            sunday: true
          }
        }
      },
      newUser: { firstName: "", lastName: "", email: "", password: "", role: "EMPLOYEE", assignedCounterId: null }
    };
  },

  computed: {
    queueForm() {
      return this.showEditQueueModal ? this.editingQueue : this.newQueue;
    },
    queueRangeInvalid() {
      if (!this.queueForm) return false;
      const min = Number(this.queueForm.rangeMin);
      const max = Number(this.queueForm.rangeMax);
      return Number.isNaN(min) || Number.isNaN(max) || min < 0 || max < 0 || min >= max;
    },
    queueRangePrefix() {
      if (!this.queueForm) return 'A';
      const mode = this.queueForm.iconDisplayMode;
      if (mode === 'number_only' || mode === 'icon_number') return '';
      return this.queueForm.prefix || 'A';
    },
    filteredUsers() {
      if (!this.userSearchQuery) return this.users;
      const query = this.userSearchQuery.toLowerCase();
      return this.users.filter(user =>
        user.firstName.toLowerCase().includes(query) ||
        user.lastName.toLowerCase().includes(query) ||
        user.email.toLowerCase().includes(query) ||
        user.role.toLowerCase().includes(query)
      );
    },
    paginatedUsers() {
      const start = (this.userCurrentPage - 1) * this.userItemsPerPage;
      const end = start + this.userItemsPerPage;
      return this.filteredUsers.slice(start, end);
    },
    selectedAgencyName() {
  const current = this.agencies.find(a => a.id === this.selectedAgencyId);
  return current ? current.nom : '';
},
filteredAgencies() {
  if (!this.agencySearchQuery) return this.agencies;
  const q = this.agencySearchQuery.toLowerCase();
  return this.agencies.filter(a => a.nom.toLowerCase().includes(q));
},


  },

  watch: {
    userSearchQuery() {
      this.userCurrentPage = 1;
    }
  },

  methods: {
    // Ajoutez cette méthode dans methods :
selectAgency(id) {
  this.selectedAgencyId = id;
  this.isAgencyDropdownOpen = false;
  this.agencySearchQuery = '';
  this.fetchData();
},
    async copyToClipboard(text) {
      try {
        await navigator.clipboard.writeText(text);
        this.copied = true;
        toast.success("Clé copiée dans le presse-papier");
        setTimeout(() => { this.copied = false; }, 2000);
      } catch (err) {
        toast.error("Impossible de copier la clé");
      }
    },

    async fetchAgencies() {
      try {
        const token = localStorage.getItem("superAdminToken");
        const res = await fetch("https://api.datafuse.fr/api/super-admin/agencies", {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = await res.json();
        this.agencies = data.map(a => ({ id: a._id, nom: a.name }));
        if (this.agencies.length > 0) {
          this.selectedAgencyId = this.agencies[0].id;
          this.fetchData();
        }
      } catch (err) { toast.error("Erreur agences"); }
    },

    async fetchData() {
      if (!this.selectedAgencyId) return;
      const token = localStorage.getItem("superAdminToken");
      const h = { Authorization: `Bearer ${token}` };
      const baseUrl = "https://api.datafuse.fr/api/super-admin"; 

      try {
        const [resC, resU, resQ] = await Promise.all([
          fetch(`${baseUrl}/counters?agencyId=${this.selectedAgencyId}`, { headers: h }), 
          fetch(`${baseUrl}/users?agencyId=${this.selectedAgencyId}`, { headers: h }),
          fetch(`${baseUrl}/queues/${this.selectedAgencyId}`, { headers: h })
        ]);

        if (!resC.ok || !resU.ok || !resQ.ok) throw new Error("Erreur de synchronisation");

        this.counters = await resC.json();
        this.users = await resU.json();
        this.queues = await resQ.json();
      } catch (err) {
        toast.error("Erreur de synchronisation des données");
      }
    },

    openEditQueueModal(queue) {
      this.editingQueue = {
        ...queue,
        rangeMin: queue.rangeMin !== undefined ? queue.rangeMin : 0,
        rangeMax: queue.rangeMax !== undefined ? queue.rangeMax : 999,
        iconDisplayMode: queue.iconDisplayMode || "icon_prefix_number",
        visibilitySchedule: queue.visibilitySchedule || {
          enabled: false,
          days: {
            monday: true,
            tuesday: true,
            wednesday: true,
            thursday: true,
            friday: true,
            saturday: true,
            sunday: true
          }
        }
      };
      this.showEditQueueModal = true;
    },

    async updateQueue() {
      if (!this.editingQueue.name) { toast.error("Nom requis"); return; }
      if (this.queueRangeInvalid) {
        toast.error("Plage invalide : le dernier numéro doit être supérieur au premier.");
        return;
      }
      try {
        const res = await fetch(`https://api.datafuse.fr/api/super-admin/queue/${this.editingQueue._id}`, {
          method: 'PUT',
          headers: this.getHeaders(),
          body: JSON.stringify({
            name: this.editingQueue.name,
            prefix: this.editingQueue.iconDisplayMode === 'number_only' ? null : (this.editingQueue.prefix ? this.editingQueue.prefix.toUpperCase() : null),
            icon: this.editingQueue.icon,
            iconDisplayMode: this.editingQueue.iconDisplayMode,
            rangeMin: this.editingQueue.rangeMin,
            rangeMax: this.editingQueue.rangeMax,
            visibilitySchedule: this.editingQueue.visibilitySchedule,
            agencyId: this.selectedAgencyId
          })
        });

        if (res.ok) {
          toast.success("File mise à jour");
          this.showEditQueueModal = false;
          this.fetchData();
        } else {
          toast.error("Erreur mise à jour");
        }
      } catch (e) { toast.error("Erreur réseau"); }
    },

    async toggleQueueToCounter(queueId) {
      const qIdStr = String(queueId);
      if (!this.selectedCounter.serviceIds) this.selectedCounter.serviceIds = [];
      const index = this.selectedCounter.serviceIds.indexOf(qIdStr);

      if (index > -1) {
        this.selectedCounter.serviceIds.splice(index, 1);
        if (String(this.selectedCounter.defaultQueueId) === qIdStr) this.selectedCounter.defaultQueueId = null;
      } else {
        this.selectedCounter.serviceIds.push(qIdStr);
      }

      try {
        const res = await fetch(`https://api.datafuse.fr/api/super-admin/counters/${this.selectedCounter._id}`, {
          method: 'PUT',
          headers: this.getHeaders(),
          body: JSON.stringify({
            name: this.selectedCounter.name,
            serviceIds: this.selectedCounter.serviceIds,
            defaultQueueId: this.selectedCounter.defaultQueueId
          })
        });
        if (res.ok) toast.success("Mise à jour réussie");
      } catch (e) { toast.error("Erreur mise à jour"); }
    },

    getQueueName(id) {
      const q = this.queues.find(q => q._id === id);
      return q ? `${q.prefix} - ${q.name}` : 'Inconnu';
    },

    async resetUserPassword(user) {
      try {
        const res = await fetch(`https://api.datafuse.fr/api/super-admin/agency/${this.selectedAgencyId}/user/${user._id}/generate-reset-key`, {
          method: 'POST',
          headers: this.getHeaders()
        });
        if (res.ok) {
          const data = await res.json();
          this.resetData = { show: true, key: data.oneTimeKey, email: data.userEmail, expiresAt: data.expiresAt };
          toast.success("Clé générée");
        }
      } catch (e) { toast.error("Erreur serveur"); }
    },

    async createQueue() {
      if (!this.newQueue.name) { toast.error("Nom requis"); return; }
      if (this.queueRangeInvalid) {
        toast.error("Plage invalide : le dernier numéro doit être supérieur au premier.");
        return;
      }
      try {
        const res = await fetch(`https://api.datafuse.fr/api/super-admin/queue`, {
          method: 'POST',
          headers: this.getHeaders(),
          body: JSON.stringify({
            ...this.newQueue,
            prefix: this.newQueue.iconDisplayMode === 'number_only' ? null : (this.newQueue.prefix ? this.newQueue.prefix.toUpperCase() : null),
            agencyId: this.selectedAgencyId
          })
        });
        if (res.ok) {
          toast.success("File créée");
          this.showAddQueueModal = false;
          this.newQueue = {
            name: "",
            prefix: "",
            icon: "pill",
            iconDisplayMode: "icon_prefix_number",
            rangeMin: 0,
            rangeMax: 999,
            visibilitySchedule: {
              enabled: false,
              days: {
                monday: true,
                tuesday: true,
                wednesday: true,
                thursday: true,
                friday: true,
                saturday: true,
                sunday: true
              }
            }
          };
          this.fetchData();
        }
      } catch (e) { toast.error("Erreur réseau"); }
    },

    async createCounter() {
      try {
        const res = await fetch(`https://api.datafuse.fr/api/super-admin/counters`, {
          method: 'POST',
          headers: this.getHeaders(),
          body: JSON.stringify({ ...this.newCounter, agencyId: this.selectedAgencyId })
        });
        if (res.ok) {
          toast.success("Guichet créé");
          this.showAddCounterModal = false;
          this.fetchData();
        }
      } catch (e) { toast.error("Erreur"); }
    },

    async updateCounter() {
      try {
        const res = await fetch(`https://api.datafuse.fr/api/super-admin/counters/${this.selectedCounter._id}`, {
          method: 'PUT',
          headers: this.getHeaders(),
          body: JSON.stringify(this.selectedCounter)
        });
        if (res.ok) {
          toast.success("Mis à jour");
          this.selectedCounter = null;
          this.fetchData();
        }
      } catch (e) { toast.error("Erreur"); }
    },

    async updateDefaultQueue() {
      try {
        await fetch(`https://api.datafuse.fr/api/super-admin/counters/${this.selectedCounter._id}`, {
          method: 'PUT',
          headers: this.getHeaders(),
          body: JSON.stringify({
            name: this.selectedCounter.name,
            defaultQueueId: this.selectedCounter.defaultQueueId,
            serviceIds: this.selectedCounter.serviceIds
          })
        });
        toast.success("File par défaut mise à jour");
        this.fetchData();
      } catch (e) { toast.error("Erreur"); }
    },

    async createUser() {
      try {
        const res = await fetch(`https://api.datafuse.fr/api/super-admin/users`, {
          method: 'POST',
          headers: this.getHeaders(),
          body: JSON.stringify({ ...this.newUser, agencyId: this.selectedAgencyId })
        });
        if (res.ok) {
          toast.success("Utilisateur créé");
          this.showAddUserModalState = false;
          this.newUser = { firstName: "", lastName: "", email: "", password: "", role: "EMPLOYEE" };
          this.fetchData();
        }
      } catch (e) { toast.error("Erreur"); }
    },

    async updateUserCounter(user, event) {
      const cid = event.target.value === "null" ? null : event.target.value;
      try {
        await fetch(`https://api.datafuse.fr/api/super-admin/users/${user._id}`, {
          method: 'PUT',
          headers: this.getHeaders(),
          body: JSON.stringify({ assignedCounterId: cid })
        });
        toast.success("Poste assigné");
        this.fetchData();
      } catch (e) { toast.error("Erreur"); }
    },

    async updateUserRole(user, event) {
      const newRole = event.target.value;
      try {
        await fetch(`https://api.datafuse.fr/api/super-admin/users/${user._id}`, {
          method: 'PUT',
          headers: this.getHeaders(),
          body: JSON.stringify({ role: newRole })
        });
        toast.success("Rôle mis à jour");
        this.fetchData();
      } catch (e) { toast.error("Erreur"); }
    },

    async deleteCounter(id) {
      if (!confirm("Supprimer ce guichet ?")) return;
      try {
        await fetch(`https://api.datafuse.fr/api/super-admin/counters/${id}`, {
          method: 'DELETE',
          headers: this.getHeaders()
        });
        toast.success("Guichet supprimé");
        this.selectedCounter = null;
        this.fetchData();
      } catch (e) { toast.error("Erreur"); }
    },

    async deleteQueue(id) {
      if (!confirm("Supprimer cette file d'attente ?")) return;
      try {
        await fetch(`https://api.datafuse.fr/api/super-admin/queue/${id}`, {
          method: 'DELETE',
          headers: this.getHeaders()
        });
        toast.success("File supprimée");
        this.fetchData();
      } catch (e) { toast.error("Erreur"); }
    },

    async deleteUser(id) {
      if (!confirm("Supprimer cet utilisateur ?")) return;
      try {
        await fetch(`https://api.datafuse.fr/api/super-admin/users/${id}`, {
          method: 'DELETE',
          headers: this.getHeaders()
        });
        toast.success("Utilisateur supprimé");
        this.fetchData();
      } catch (e) { toast.error("Erreur"); }
    },

    formatDate(dateStr) {
      if (!dateStr) return "";
      return new Date(dateStr).toLocaleDateString('fr-FR', {
        day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
      });
    },

    getHeaders() {
      return { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem("superAdminToken")}` };
    },

    getCounterUsers(id) {
      return this.users.filter(u => u.assignedCounterId === id);
    },

    openEditDrawer(counter) { 
      const cloned = JSON.parse(JSON.stringify(counter));
      cloned.serviceIds = (cloned.serviceIds || []).map(id => String(id));
      cloned.defaultQueueId = cloned.defaultQueueId ? String(cloned.defaultQueueId) : null;
      this.selectedCounter = cloned;
    }
  },

  mounted() {
    this.fetchAgencies();
  }
};
</script>